import moment, { Moment } from 'moment';
import { RELATIVE } from 'src/constants/dates';
import { IPassengerShifts, IPickupDropOrder } from 'src/types/manualOrders/api.types';
import { IAddressWithIdAndIsDefault } from 'src/types/misc';

export interface AddressValues {
    city: string;
    street: string;
    houseNum: string;
}

export interface EditedOrderAddresses {
    pickup: AddressValues | null;
    drop: AddressValues | null;
}

export const NO_ORDER_ADDRESSES: EditedOrderAddresses = { pickup: null, drop: null };

/**
 * The order's own address, or null when it has none: "city" missing (a database without D-058) or ""
 * (an order saved without one). Decided by city only — a valid address may have an empty street.
 */
const addressOfOrder = (order?: IPickupDropOrder): AddressValues | null =>
    order?.city?.trim() ? { city: order.city, street: order.street ?? '', houseNum: order.houseNum ?? '' } : null;

const findOrder = (orders: IPickupDropOrder[] = [], orderCodes: string[], shiftTime?: string) =>
    orders.find((o) => orderCodes.includes(String(o.orderCode))) ??
    (shiftTime ? orders.find((o) => o.shiftTime === shiftTime) : undefined);

/**
 * The pickup and drop addresses saved on the orders being edited (ws_MyWayStudio_Get_Passsengers_4_Dates).
 * The orders are found by the codes the edited cell holds, else by shift time — two orders on one day happen.
 */
const getOrderAddresses = ({
    passenger,
    date,
    orderCodes,
    pickupTime,
    dropTime,
}: {
    passenger?: IPassengerShifts;
    date?: Date | Moment | string | null;
    orderCodes?: string[];
    pickupTime?: string;
    dropTime?: string;
}): EditedOrderAddresses => {
    if (!passenger?.dates || !date) return NO_ORDER_ADDRESSES;

    const relativeDate = moment(date).format(RELATIVE);
    const day = passenger.dates.find((d) => d.relativeDate === relativeDate);
    if (!day) return NO_ORDER_ADDRESSES;

    const codes = (orderCodes || []).map(String);
    return {
        pickup: addressOfOrder(findOrder(day.pickupOrders, codes, pickupTime)),
        drop: addressOfOrder(findOrder(day.dropOrders, codes, dropTime)),
    };
};

// Exact after trimming: matching "" to "0" would save "0" for an order saved with "" — saving must not change
const isSameAddress = (a: AddressValues, b: AddressValues): boolean =>
    String(a.city ?? '').trim() === String(b.city ?? '').trim() &&
    String(a.street ?? '').trim() === String(b.street ?? '').trim() &&
    String(a.houseNum ?? '').trim() === String(b.houseNum ?? '').trim();

/**
 * The drop-down option for the order's address: the matching option if there is one; otherwise the
 * order's address added as an option of its own — it is what was saved, so it is shown as it is.
 */
const selectOrderAddressOption = (
    options: IAddressWithIdAndIsDefault[],
    orderAddress: AddressValues,
    type: 'pickup' | 'drop'
): { options: IAddressWithIdAndIsDefault[]; selected: IAddressWithIdAndIsDefault } => {
    const match = options.find((option) => isSameAddress(option, orderAddress));
    if (match) return { options, selected: match };

    const added: IAddressWithIdAndIsDefault = { id: `order-address-${type}`, isDefault: false, ...orderAddress };
    return { options: [...options, added], selected: added };
};

/**
 * What a drop-down offers and pre-selects: the order's own address when the edited order has one,
 * otherwise the passenger's default option — today's behaviour, and the only one on a database without D-058.
 */
const chooseAddressForType = (
    options: IAddressWithIdAndIsDefault[],
    orderAddress: AddressValues | null,
    type: 'pickup' | 'drop'
): { options: IAddressWithIdAndIsDefault[]; selected: IAddressWithIdAndIsDefault | undefined } => {
    if (orderAddress) return selectOrderAddressOption(options, orderAddress, type);
    return { options, selected: options.find((address) => address.isDefault) };
};

const orderAddressHelpers = { getOrderAddresses, isSameAddress, selectOrderAddressOption, chooseAddressForType };

export default orderAddressHelpers;
